import React, { useState, useRef, useEffect, useContext } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Trash2, Play, Clock, User, Music, Image as ImageIcon } from 'lucide-react';
import { API } from 'aws-amplify';
import Hls from 'hls.js';
import Context from '../../../../Context/Context';
import Modal from './DeleteModal';

// Enhanced utility function to clean and encode thumbnail URLs for iOS compatibility
const sanitizeThumbnailUrl = (url) => {
  if (!url) return null;
  
  try {
    // Handle different URL formats
    let cleanUrl = url.trim();
    
    // If URL is already encoded, decode it first
    if (cleanUrl.includes('%')) {
      try {
        cleanUrl = decodeURIComponent(cleanUrl);
      } catch (decodeError) {
        console.warn('Failed to decode URL, using original:', decodeError);
      }
    }
    
    // Properly encode the URL for iOS compatibility
    const encodedUrl = cleanUrl
      .replace(/\s+/g, '%20')
      .replace(/[^\w\-._~:/?#[\]@!$&'()*+,;=]/g, (match) => {
        return encodeURIComponent(match);
      });
    
    // Validate URL format
    try {
      new URL(encodedUrl);
      return encodedUrl;
    } catch (urlError) {
      console.error('Invalid URL format:', encodedUrl);
      return null;
    }
  } catch (error) {
    console.error('Error sanitizing thumbnail URL:', error);
    return null;
  }
};

// iOS-specific image loading utility
const createImageLoadPromise = (url, timeout = 8000) => {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    const timer = setTimeout(() => {
      img.onload = null;
      img.onerror = null;
      reject(new Error('Image load timeout'));
    }, timeout);
    
    img.onload = () => {
      clearTimeout(timer);
      img.onload = null;
      img.onerror = null;
      resolve(url);
    };
    
    img.onerror = (error) => {
      clearTimeout(timer);
      img.onload = null;
      img.onerror = null;
      reject(new Error(`Image failed to load: ${error.message || 'Unknown error'}`));
    };
    
    // Try loading without crossOrigin first for same-origin images
    try {
      img.src = url;
    } catch (error) {
      clearTimeout(timer);
      reject(new Error(`Invalid image URL: ${error.message}`));
    }
  });
};

const VideoCard = ({ video, onClick, onVideoUpdate, viewMode = 'grid' }) => {
  const [isHovering, setIsHovering] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [thumbnailState, setThumbnailState] = useState({
    loading: true,
    error: false,
    loaded: false,
    currentUrl: null
  });
  
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const { userData, fetchPlaylists, setVideos } = useContext(Context);

  // Default thumbnail URL
  const defaultThumbnailUrl = "https://bworkz-institution-utils.s3.us-east-2.amazonaws.com/public/images/default+bworkz.png";

  // Generate multiple fallback URLs for iOS compatibility
  const generateFallbackUrls = (originalUrl) => {
    if (!originalUrl) return [defaultThumbnailUrl];
    
    const urls = [];
    
    // Add sanitized URL
    const sanitized = sanitizeThumbnailUrl(originalUrl);
    if (sanitized) {
      urls.push(sanitized);
    }
    
    // Add original URL as fallback
    if (originalUrl !== sanitized) {
      urls.push(originalUrl);
    }
    
    // Add URL with different encoding strategies
    try {
      const simpleEncoded = originalUrl.replace(/\s+/g, '%20');
      if (!urls.includes(simpleEncoded)) {
        urls.push(simpleEncoded);
      }
      
      const fullyEncoded = encodeURI(originalUrl);
      if (!urls.includes(fullyEncoded)) {
        urls.push(fullyEncoded);
      }
    } catch (error) {
      console.warn('Error creating fallback URLs:', error);
    }
    
    // Always include default as final fallback
    urls.push(defaultThumbnailUrl);
    
    return [...new Set(urls)]; // Remove duplicates
  };

  // Initialize thumbnail loading
  useEffect(() => {
    const loadThumbnail = async () => {
      const fallbackUrls = generateFallbackUrls(video.thumbnailUrl);
      
      console.log(`Starting thumbnail load for "${video.title}" with ${fallbackUrls.length} URLs:`, fallbackUrls);
      
      setThumbnailState({
        loading: true,
        error: false,
        loaded: false,
        currentUrl: null
      });

      // Try each URL until one works
      for (let i = 0; i < fallbackUrls.length; i++) {
        const url = fallbackUrls[i];
        console.log(`Trying URL ${i + 1}/${fallbackUrls.length} for "${video.title}":`, url);
        
        try {
          await createImageLoadPromise(url, 6000); // 6 second timeout per URL
          
          setThumbnailState({
            loading: false,
            error: false,
            loaded: true,
            currentUrl: url
          });
          
          console.log(`✅ Thumbnail loaded successfully for "${video.title}" using URL:`, url);
          return;
        } catch (error) {
          console.warn(`❌ Failed to load thumbnail for "${video.title}" with URL:`, url, error.message);
          
          // If this is the last URL and it's not the default, try a simpler approach
          if (i === fallbackUrls.length - 1 && url !== defaultThumbnailUrl) {
            console.log(`Trying simple default thumbnail for "${video.title}"`);
            try {
              await createImageLoadPromise(defaultThumbnailUrl, 3000);
              setThumbnailState({
                loading: false,
                error: false,
                loaded: true,
                currentUrl: defaultThumbnailUrl
              });
              console.log(`✅ Default thumbnail loaded for "${video.title}"`);
              return;
            } catch (defaultError) {
              console.error(`❌ Even default thumbnail failed for "${video.title}":`, defaultError.message);
            }
          }
          continue;
        }
      }
      
      // All URLs failed - set error state but still try to show something
      console.error(`🚫 All thumbnail URLs failed for video: "${video.title}"`);
      setThumbnailState({
        loading: false,
        error: true,
        loaded: false,
        currentUrl: defaultThumbnailUrl // Still try to show default
      });
    };

    if (video.title) {
      loadThumbnail();
    }
  }, [video.thumbnailUrl, video.title]);

  // Setup HLS when component mounts
  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement) return;

    const encodedUrl = video.videoUrl.replace(/\s+/g, '%20');

    const setupHLS = () => {
      if (Hls.isSupported()) {
        const hls = new Hls({
          enableWorker: false,
          lowLatencyMode: true,
          backBufferLength: 90,
          autoStartLoad: false
        });
        
        hlsRef.current = hls;
        hls.loadSource(encodedUrl);
        hls.attachMedia(videoElement);
        
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          setVideoLoaded(true);
        });

        hls.on(Hls.Events.ERROR, (event, data) => {
          if (data.fatal) {
            console.error('HLS fatal error:', data);
            setVideoLoaded(false);
          }
        });
      } else if (videoElement.canPlayType('application/vnd.apple.mpegurl')) {
        videoElement.src = encodedUrl;
        videoElement.addEventListener('loadedmetadata', () => {
          setVideoLoaded(true);
        });
      }
    };

    setupHLS();

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [video.videoUrl]);

  const handleMouseEnter = () => {
    setIsHovering(true);
    if (videoRef.current && videoLoaded) {
      if (hlsRef.current && !hlsRef.current.media.readyState) {
        hlsRef.current.startLoad();
      }
      
      const playPromise = videoRef.current.play();
      if (playPromise !== undefined) {
        playPromise.catch(error => {
          console.log('Video playback failed:', error);
        });
      }
    }
  };

  const handleMouseLeave = () => {
    setIsHovering(false);
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  };

  const handleDelete = async () => {
    if (isDeleting) return;
  
    try {
      setIsDeleting(true);
      
      const response = await API.del('user', '/admin/delete-class-videos/Bworkz', {
        body: {
          videoType_partNo: video.videoType_partNo,
          videoUrl: video.videoUrl,
          thumbnailUrl: video.thumbnailUrl
        },
        headers: {
          'Content-Type': 'application/json'
        }
      });
  
      if (response) {
        onVideoUpdate(video.videoUrl);
        
        setVideos(prevVideos => {
          return prevVideos.filter(v => v.videoUrl !== video.videoUrl);
        });

        await fetchPlaylists();
      }
    } catch (error) {
      console.error('Delete operation failed:', error);
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };
  
  const getFormattedDate = (timestamp) => {
    const parsedTimestamp = parseInt(timestamp);
    if (isNaN(parsedTimestamp)) return "Unknown";
    
    const date = new Date(parsedTimestamp);
    return !isNaN(date.getTime())
      ? formatDistanceToNow(date, { addSuffix: true })
      : "Unknown";
  };

  const uploadTimeAgo = getFormattedDate(video.uploadDate);
  const isDarkMode = useContext(Context).isDarkMode ?? true;

  // Enhanced Thumbnail component with better iOS support
  const ThumbnailComponent = ({ className = "" }) => {
    const { loading, error, loaded, currentUrl } = thumbnailState;
    
    return (
      <div className={`relative w-full h-full overflow-hidden ${className}`}>
        {/* Loading state */}
        {loading && (
          <div className={`absolute inset-0 flex items-center justify-center z-10 ${isDarkMode ? 'bg-[#2a2a2a]' : 'bg-gray-200'}`}>
            <div className={`animate-spin rounded-full h-8 w-8 border-b-2 ${isDarkMode ? 'border-[#FDCF08]' : 'border-blue-600'}`}></div>
          </div>
        )}
        
        {/* Main thumbnail image */}
        {currentUrl && !loading && (
          <>
            <img
              src={currentUrl}
              alt={video.title || 'Video thumbnail'}
              className="w-full h-full object-cover transition-all duration-300 hover:scale-105"
              loading="lazy"
              onError={(e) => {
                console.error(`Image render error for "${video.title}":`, e);
                if (!error) {
                  setThumbnailState(prev => ({
                    ...prev,
                    error: true,
                    loaded: false
                  }));
                }
              }}
              onLoad={() => {
                console.log(`Image successfully rendered for "${video.title}"`);
                if (!loaded) {
                  setThumbnailState(prev => ({
                    ...prev,
                    loaded: true,
                    error: false
                  }));
                }
              }}
              style={{
                imageRendering: 'auto',
                WebkitImageSmoothing: true
              }}
            />
            
            {/* Gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300" />
            
            {/* Play button overlay */}
            <div className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity duration-300">
              <div className={`backdrop-blur-sm rounded-full p-3 md:p-4 shadow-xl transform scale-90 hover:scale-100 transition-transform duration-200 ${
                isDarkMode ? 'bg-[#FDCF08]/90' : 'bg-white/90'
              }`}>
                <Play className={`w-5 h-5 md:w-6 md:h-6 ${isDarkMode ? 'text-black' : 'text-gray-800'}`} fill="currentColor" />
              </div>
            </div>
          </>
        )}
        
        {/* Error state fallback */}
        {error && !loading && (
          <div className={`absolute inset-0 flex flex-col items-center justify-center border-2 border-dashed ${
            isDarkMode
              ? 'bg-[#2a2a2a] border-[#2a2a2a] text-gray-500'
              : 'bg-gradient-to-br from-gray-100 to-gray-200 border-gray-300 text-gray-500'
          }`}>
            <ImageIcon className={`w-6 h-6 md:w-8 md:h-8 mb-2 ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`} />
            <span className={`text-xs text-center px-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>Image unavailable</span>
          </div>
        )}
      </div>
    );
  };

  // Different card layouts based on view mode
  if (viewMode === 'list') {
    return (
      <div 
        className={`relative rounded-lg border transition-all duration-200 cursor-pointer ${
          isDarkMode
            ? 'bg-[#1a1a1a] border-[#2a2a2a] hover:border-[#FDCF08] hover:bg-[#222222]'
            : 'bg-white border-gray-200 hover:border-[#FDCF08] hover:shadow-md'
        }`}
        onClick={() => onClick(video)}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <div className="flex items-center p-3 space-x-3">
          {/* Thumbnail */}
          <div className="relative flex-shrink-0">
            <div className={`w-20 h-12 rounded-lg overflow-hidden ${isDarkMode ? 'bg-[#2a2a2a]' : 'bg-gray-200'}`}>
              <ThumbnailComponent />
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0 w-full">
            <h3 className={`font-semibold text-sm mb-1 overflow-hidden text-ellipsis whitespace-nowrap w-full ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`} title={video.title}>
              {video.title}
            </h3>
            <p className={`text-xs mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              <User className={`inline w-3 h-3 mr-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`} />
              {video.choreographer}
            </p>
            <p className={`text-xs mb-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
              <Music className={`inline w-3 h-3 mr-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`} />
              {video.songName}
            </p>
            <div className={`flex items-center text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              <Clock className={`w-3 h-3 mr-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
              {uploadTimeAgo}
            </div>
          </div>

          {/* Delete button for admin */}
          {userData.userType === 'admin' && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setShowDeleteModal(true);
              }}
              disabled={isDeleting}
              className={`p-1 rounded-lg transition-colors ${
                isDarkMode
                  ? 'text-gray-400 hover:text-red-400 hover:bg-[#2a2a2a]'
                  : 'text-gray-400 hover:text-red-500 hover:bg-red-50'
              }`}
            >
              <Trash2 size={14} />
            </button>
          )}
        </div>

        {showDeleteModal && (
          <Modal
            title="Confirm Delete"
            message="Are you sure you want to delete this video?"
            onConfirm={handleDelete}
            onCancel={() => setShowDeleteModal(false)}
            isDeleting={isDeleting}
          />
        )}
      </div>
    );
  }

  // Grid and Masonry view (default)
  return (
    <div 
      className={`relative rounded-lg transition-all duration-200 cursor-pointer overflow-hidden flex-shrink-0 w-full ${
        isDarkMode
          ? 'border-none bg-[#1a1a1a] border-[#2a2a2a] hover:border-[#FDCF08] hover:bg-[#222222]'
          : 'border bg-white border-gray-200 hover:border-[#FDCF08] hover:shadow-md'
      }`}
      onClick={() => onClick(video)}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {/* Thumbnail */}
      <div className={`relative w-full aspect-video ${isDarkMode ? 'bg-[#2a2a2a]' : 'bg-gray-200'}`}>
        <ThumbnailComponent />
        
        {/* Delete button for admin */}
        {userData.userType === 'admin' && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setShowDeleteModal(true);
            }}
            disabled={isDeleting}
            className={`absolute top-2 right-2 p-1.5 rounded-full transition-all duration-200 shadow-lg z-10 ${
              isDarkMode
                ? 'bg-[#1a1a1a]/90 hover:bg-red-500/20 text-gray-400 hover:text-red-400'
                : 'bg-white/90 hover:bg-red-50 text-gray-400 hover:text-red-500'
            }`}
          >
            <Trash2 size={14} />
          </button>
        )}
      </div>

      {/* Content */}
      <div className="p-3 md:p-4 w-full">
        <h3 className={`font-semibold text-sm md:text-base mb-2.5 overflow-hidden text-ellipsis whitespace-nowrap w-full ${
          isDarkMode ? 'text-white' : 'text-gray-900'
        }`} title={video.title}>
          {video.title}
        </h3>
        
        <div className="space-y-1.5 text-xs md:text-sm">
          <div className={`flex items-center ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            <Music className={`w-3 h-3 md:w-4 md:h-4 mr-1.5 flex-shrink-0 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`} />
            <span className="truncate">{video.songName || 'No song name'}</span>
          </div>
          <div className={`flex items-center ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            <User className={`w-3 h-3 md:w-4 md:h-4 mr-1.5 flex-shrink-0 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
            <span className="truncate">{video.choreographer || 'Unknown'}</span>
          </div>
          <div className={`flex items-center ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            <Clock className={`w-3 h-3 md:w-4 md:h-4 mr-1.5 flex-shrink-0 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
            <span>{uploadTimeAgo}</span>
          </div>
        </div>
      </div>

      {showDeleteModal && (
        <Modal
          title="Confirm Delete"
          message="Are you sure you want to delete this video?"
          onConfirm={handleDelete}
          onCancel={() => setShowDeleteModal(false)}
          isDeleting={isDeleting}
        />
      )}
    </div>
  );
};

export default VideoCard;